import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import {
  getHallBackgroundColors,
  MAX_TABLE_SIZE,
  MIN_TABLE_SIZE,
  normalizeOutline,
  placementsOverlap,
} from './lib/defaults';
import FloorObjectGlyph from './FloorObjectGlyph';
import TableSeats from './TableSeats';
import { DEFAULT_TABLE_COLOR } from './floorPlanStorage';
import { FP, fpCard } from './lib/ui';

const clampTableDim = (value) =>
  Math.min(MAX_TABLE_SIZE, Math.max(MIN_TABLE_SIZE, value));

const shapeRadius = (shape, width, height) => {
  if (shape === 'round') {
    return Math.min(width, height) / 2;
  }
  if (shape === 'booth') {
    return 14;
  }
  return 12;
};

const HANDLE_SIZE = 10;

/** Corner resize grips — opposite corner stays fixed while dragging. */
const RESIZE_CORNERS = [
  { id: 'nw', top: true, left: true, cursor: 'nwse-resize' },
  { id: 'ne', top: true, left: false, cursor: 'nesw-resize' },
  { id: 'sw', top: false, left: true, cursor: 'nesw-resize' },
  { id: 'se', top: false, left: false, cursor: 'nwse-resize' },
];

const cornerHandleSx = (corner) => ({
  position: 'absolute',
  ...(corner.top
    ? { top: -HANDLE_SIZE / 2 }
    : { bottom: -HANDLE_SIZE / 2 }),
  ...(corner.left
    ? { left: -HANDLE_SIZE / 2 }
    : { right: -HANDLE_SIZE / 2 }),
  width: HANDLE_SIZE,
  height: HANDLE_SIZE,
  bgcolor: '#2D7FF9',
  border: '1px solid #fff',
  borderRadius: 0.5,
  cursor: corner.cursor,
  zIndex: 2,
});

const resizeFromCorner = (orig, corner, dx, dy, clampDim) => {
  let width = orig.width;
  let height = orig.height;
  let x = orig.x;
  let y = orig.y;

  if (corner.includes('e')) {
    width = clampDim(orig.width + dx);
  } else if (corner.includes('w')) {
    width = clampDim(orig.width - dx);
    x = orig.x + (orig.width - width);
  }

  if (corner.includes('s')) {
    height = clampDim(orig.height + dy);
  } else if (corner.includes('n')) {
    height = clampDim(orig.height - dy);
    y = orig.y + (orig.height - height);
  }

  return { x, y, width, height };
};

/**
 * Web floor canvas: zoom/pan, tables + objects, resize/rotate handles.
 */
const FloorPlanCanvas = ({
  layout,
  tables = [],
  sectionUuid,
  selectedTableUuid,
  selectedObjectId,
  onSelectTable,
  onSelectObject,
  onUpdateTablePlacement,
  onUpdateFloorObject,
  snapshotHistory,
  outlineEdit = false,
  onUpdateOutline,
  /** Resize logical canvas to the hall viewport (rescale placements). */
  onFillToViewport,
}) => {
  const viewportRef = useRef(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [spaceDown, setSpaceDown] = useState(false);
  const dragRef = useRef(null);
  const panRef = useRef(null);
  /** Auto-fill once per section so tall/narrow saved halls expand to workspace. */
  const autoFilledSectionRef = useRef(null);

  const canvasW = layout?.canvas?.width || 1200;
  const canvasH = layout?.canvas?.height || 800;
  const hallColors = getHallBackgroundColors(layout?.style?.background);
  const outline = normalizeOutline(layout?.outline);
  const showGrid = layout?.style?.showGrid !== false;
  const gridSize = layout?.style?.gridSize || 20;

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) {
      return undefined;
    }
    const measure = () => {
      setViewport({ width: node.clientWidth, height: node.clientHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [layout?.canvas?.width, layout?.canvas?.height]);

  const fillToViewport = useCallback(() => {
    if (!onFillToViewport || !viewport.width || !viewport.height) {
      return false;
    }
    const nextW = Math.max(200, Math.round(viewport.width));
    const nextH = Math.max(160, Math.round(viewport.height));
    if (
      Math.abs(nextW - canvasW) <= 2 &&
      Math.abs(nextH - canvasH) <= 2
    ) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return true;
    }
    onFillToViewport({ width: nextW, height: nextH });
    setZoom(1);
    setPan({ x: 0, y: 0 });
    return true;
  }, [onFillToViewport, viewport.width, viewport.height, canvasW, canvasH]);

  // Tall/narrow saved canvases (e.g. 1200×1862) letterbox inside a wide hall
  // workspace — sync logical size to the viewport so the floor fills the area.
  useEffect(() => {
    if (!onFillToViewport || !sectionUuid || !viewport.width || !viewport.height) {
      return;
    }
    if (autoFilledSectionRef.current === sectionUuid) {
      return;
    }
    const scale = Math.min(viewport.width / canvasW, viewport.height / canvasH);
    const usedW = canvasW * scale;
    const usedH = canvasH * scale;
    const fillsWell =
      usedW / viewport.width >= 0.92 && usedH / viewport.height >= 0.92;
    if (fillsWell) {
      autoFilledSectionRef.current = sectionUuid;
      return;
    }
    autoFilledSectionRef.current = sectionUuid;
    fillToViewport();
  }, [
    onFillToViewport,
    sectionUuid,
    viewport.width,
    viewport.height,
    canvasW,
    canvasH,
    fillToViewport,
  ]);

  const fitZoom = useMemo(() => {
    if (!viewport.width || !viewport.height) {
      return 1;
    }
    // Edge-to-edge inside the hall chrome once canvas matches the viewport.
    return Math.min(viewport.width / canvasW, viewport.height / canvasH, 3);
  }, [viewport.width, viewport.height, canvasW, canvasH]);

  useEffect(() => {
    setZoom(fitZoom);
    setPan({ x: 0, y: 0 });
  }, [fitZoom, layout?.canvas?.width, layout?.canvas?.height]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space') {
        setSpaceDown(true);
      }
      if (!selectedTableUuid && !selectedObjectId) {
        return;
      }
      const step = e.shiftKey ? 10 : 2;
      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowLeft') dx = -step;
      if (e.key === 'ArrowRight') dx = step;
      if (e.key === 'ArrowUp') dy = -step;
      if (e.key === 'ArrowDown') dy = step;
      if (!dx && !dy) {
        return;
      }
      e.preventDefault();
      if (selectedTableUuid) {
        const p = layout?.tables?.[selectedTableUuid];
        if (!p) return;
        onUpdateTablePlacement?.(
          selectedTableUuid,
          { x: p.x + dx, y: p.y + dy },
          { snap: false, recordHistory: true }
        );
      } else if (selectedObjectId) {
        const o = layout?.objects?.[selectedObjectId];
        if (!o) return;
        onUpdateFloorObject?.(
          selectedObjectId,
          { x: o.x + dx, y: o.y + dy },
          { snap: false, recordHistory: true }
        );
      }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space') {
        setSpaceDown(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [
    selectedTableUuid,
    selectedObjectId,
    layout,
    onUpdateTablePlacement,
    onUpdateFloorObject,
  ]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.min(3, Math.max(0.25, z * delta)));
  }, []);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;
    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const tableVisuals = useMemo(() => {
    if (!layout) return [];
    return tables
      .map((table) => {
        const uuid = table?.tableDetails?.uuid;
        if (!uuid) return null;
        const placement = layout.tables?.[uuid];
        if (!placement) return null;
        return { table, placement, uuid };
      })
      .filter(Boolean);
  }, [layout, tables]);

  const objects = useMemo(() => Object.values(layout?.objects || {}), [layout]);

  const collisionUuids = useMemo(() => {
    const hits = new Set();
    for (let i = 0; i < tableVisuals.length; i += 1) {
      for (let j = i + 1; j < tableVisuals.length; j += 1) {
        if (
          placementsOverlap(
            tableVisuals[i].placement,
            tableVisuals[j].placement
          )
        ) {
          hits.add(tableVisuals[i].uuid);
          hits.add(tableVisuals[j].uuid);
        }
      }
    }
    return hits;
  }, [tableVisuals]);

  const startPan = (clientX, clientY) => {
    panRef.current = {
      startX: clientX,
      startY: clientY,
      origX: pan.x,
      origY: pan.y,
    };
  };

  const onViewportPointerDown = (e) => {
    if (e.button === 1 || spaceDown || e.button === 2) {
      e.preventDefault();
      startPan(e.clientX, e.clientY);
      return;
    }
    if (e.target === e.currentTarget || e.target?.dataset?.floor === '1') {
      onSelectTable?.(null);
      onSelectObject?.(null);
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      if (panRef.current) {
        const dx = e.clientX - panRef.current.startX;
        const dy = e.clientY - panRef.current.startY;
        setPan({
          x: panRef.current.origX + dx,
          y: panRef.current.origY + dy,
        });
        return;
      }
      const drag = dragRef.current;
      if (!drag) return;
      const dx = (e.clientX - drag.startX) / zoom;
      const dy = (e.clientY - drag.startY) / zoom;
      if (drag.kind === 'move-table') {
        onUpdateTablePlacement?.(
          drag.id,
          { x: drag.orig.x + dx, y: drag.orig.y + dy },
          { snap: false, recordHistory: false }
        );
      } else if (drag.kind === 'move-object') {
        onUpdateFloorObject?.(
          drag.id,
          { x: drag.orig.x + dx, y: drag.orig.y + dy },
          { snap: false, recordHistory: false }
        );
      } else if (drag.kind === 'resize-table') {
        onUpdateTablePlacement?.(
          drag.id,
          resizeFromCorner(
            drag.orig,
            drag.corner || 'se',
            dx,
            dy,
            clampTableDim
          ),
          { snap: false, recordHistory: false }
        );
      } else if (drag.kind === 'resize-object') {
        const clampObjectDim = (value) =>
          Math.max(MIN_TABLE_SIZE * 0.5, value);
        onUpdateFloorObject?.(
          drag.id,
          resizeFromCorner(
            drag.orig,
            drag.corner || 'se',
            dx,
            dy,
            clampObjectDim
          ),
          { snap: false, recordHistory: false }
        );
      } else if (drag.kind === 'rotate-table') {
        const angle =
          (Math.atan2(e.clientY - drag.centerY, e.clientX - drag.centerX) *
            180) /
          Math.PI;
        onUpdateTablePlacement?.(
          drag.id,
          { rotation: Math.round(angle / 15) * 15 },
          { snap: false, recordHistory: false }
        );
      } else if (drag.kind === 'vertex') {
        const rect = viewportRef.current?.getBoundingClientRect();
        if (!rect) return;
        const localX =
          (e.clientX - rect.left - viewport.width / 2 - pan.x) / zoom +
          canvasW / 2;
        const localY =
          (e.clientY - rect.top - viewport.height / 2 - pan.y) / zoom +
          canvasH / 2;
        const next = outline.map((p, idx) =>
          idx === drag.index
            ? {
                x: Math.min(1, Math.max(0, localX / canvasW)),
                y: Math.min(1, Math.max(0, localY / canvasH)),
              }
            : p
        );
        onUpdateOutline?.(next);
      }
    };
    const onUp = () => {
      dragRef.current = null;
      panRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [
    zoom,
    pan,
    viewport,
    canvasW,
    canvasH,
    outline,
    layout,
    onUpdateTablePlacement,
    onUpdateFloorObject,
    onUpdateOutline,
  ]);

  if (!layout) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F8FAFC',
          borderRadius: 2,
          border: '1px solid rgba(15,23,42,0.08)',
        }}
      >
        <Typography color="text.secondary">Select a section to edit</Typography>
      </Box>
    );
  }

  const outlinePoints = outline
    .map((p) => `${p.x * canvasW},${p.y * canvasH}`)
    .join(' ');

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 1 }}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{
            ...fpCard,
            borderRadius: 2,
            px: 0.5,
            py: 0.25,
            boxShadow: FP.shadowSm,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setZoom((z) => Math.max(0.25, z * 0.9))}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            sx={{
              minWidth: 44,
              textAlign: 'center',
              fontWeight: 700,
              color: FP.ink,
            }}
          >
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton
            size="small"
            onClick={() => setZoom((z) => Math.min(3, z * 1.1))}
          >
            <AddIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              if (!fillToViewport()) {
                setZoom(fitZoom);
                setPan({ x: 0, y: 0 });
              }
            }}
            title="Fill hall area"
          >
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="caption" sx={{ color: FP.faint }}>
          Scroll to zoom · Space/middle-drag to pan · Arrows to nudge
        </Typography>
      </Stack>

      <Box
        ref={viewportRef}
        onPointerDown={onViewportPointerDown}
        onContextMenu={(e) => e.preventDefault()}
        sx={{
          flex: 1,
          minHeight: 480,
          overflow: 'hidden',
          borderRadius: 3,
          border: `1px solid ${FP.line}`,
          bgcolor: '#E2E8F0',
          backgroundImage:
            'radial-gradient(rgba(15,23,42,0.06) 1px, transparent 1px)',
          backgroundSize: '16px 16px',
          cursor: spaceDown ? 'grab' : 'default',
          position: 'relative',
        }}
      >
        <Box
          data-floor="1"
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: canvasW,
            height: canvasH,
            transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            bgcolor: hallColors.color,
            boxShadow: '0 8px 32px rgba(15,23,42,0.12)',
            backgroundImage: showGrid
              ? `linear-gradient(${hallColors.patternColor} 1px, transparent 1px), linear-gradient(90deg, ${hallColors.patternColor} 1px, transparent 1px)`
              : 'none',
            backgroundSize: showGrid
              ? `${gridSize}px ${gridSize}px`
              : undefined,
          }}
        >
          <svg
            width={canvasW}
            height={canvasH}
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: outlineEdit ? 'auto' : 'none',
            }}
          >
            <polygon
              points={outlinePoints}
              fill="rgba(255,255,255,0.08)"
              stroke="#64748B"
              strokeWidth={2}
              strokeDasharray={outlineEdit ? '6 4' : undefined}
            />
            {outlineEdit
              ? outline.map((p, index) => (
                  <circle
                    key={index}
                    cx={p.x * canvasW}
                    cy={p.y * canvasH}
                    r={8}
                    fill="#2D7FF9"
                    stroke="#fff"
                    strokeWidth={2}
                    style={{ cursor: 'move' }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      dragRef.current = { kind: 'vertex', index };
                    }}
                  />
                ))
              : null}
          </svg>

          {objects.map((object) => {
            const selected = selectedObjectId === object.id;
            return (
              <Box
                key={object.id}
                onPointerDown={(e) => {
                  if (spaceDown || e.button === 1) return;
                  e.stopPropagation();
                  onSelectObject?.(object.id);
                  snapshotHistory?.();
                  dragRef.current = {
                    kind: 'move-object',
                    id: object.id,
                    startX: e.clientX,
                    startY: e.clientY,
                    orig: { ...object },
                  };
                }}
                sx={{
                  position: 'absolute',
                  left: object.x,
                  top: object.y,
                  width: object.width,
                  height: object.height,
                  transform: `rotate(${object.rotation || 0}deg)`,
                  zIndex: object.zIndex || 1,
                  cursor: 'move',
                }}
              >
                <FloorObjectGlyph object={object} selected={selected} />
                {selected
                  ? RESIZE_CORNERS.map((corner) => (
                      <Box
                        key={corner.id}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          snapshotHistory?.();
                          dragRef.current = {
                            kind: 'resize-object',
                            id: object.id,
                            corner: corner.id,
                            startX: e.clientX,
                            startY: e.clientY,
                            orig: { ...object },
                          };
                        }}
                        sx={cornerHandleSx(corner)}
                      />
                    ))
                  : null}
              </Box>
            );
          })}

          {tableVisuals.map(({ table, placement, uuid }) => {
            const selected = selectedTableUuid === uuid;
            const colliding = collisionUuids.has(uuid);
            const name =
              table.tableDetails?.displayName ||
              table.tableDetails?.name ||
              'T';
            const radius = shapeRadius(
              placement.shape,
              placement.width,
              placement.height
            );
            const minSide = Math.min(placement.width, placement.height);
            const nameSize = Math.max(16, Math.round(minSide * 0.26));
            const seatsSize = Math.max(13, Math.round(nameSize * 0.62));
            const capacity = Number(table.tableDetails?.capacity) || 0;
            return (
              <Box
                key={uuid}
                onPointerDown={(e) => {
                  if (spaceDown || e.button === 1) return;
                  e.stopPropagation();
                  onSelectTable?.(uuid);
                  snapshotHistory?.();
                  dragRef.current = {
                    kind: 'move-table',
                    id: uuid,
                    startX: e.clientX,
                    startY: e.clientY,
                    orig: { ...placement },
                  };
                }}
                sx={{
                  position: 'absolute',
                  left: placement.x,
                  top: placement.y,
                  width: placement.width,
                  height: placement.height,
                  transform: `rotate(${placement.rotation || 0}deg)`,
                  zIndex: (placement.zIndex || 1) + 10,
                  cursor: 'move',
                }}
              >
                <TableSeats
                  shape={placement.shape}
                  width={placement.width}
                  height={placement.height}
                  capacity={capacity}
                  selected={selected}
                />
                <Box
                  sx={{
                    boxSizing: 'border-box',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    bgcolor: colliding
                      ? '#FEE2E2'
                      : placement.color || DEFAULT_TABLE_COLOR,
                    border: `2px solid ${
                      selected ? '#2D7FF9' : colliding ? '#EF4444' : '#94A3B8'
                    }`,
                    borderRadius: `${radius}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: selected
                      ? '0 0 0 3px rgba(45,127,249,0.2)'
                      : '0 2px 8px rgba(15,23,42,0.08)',
                    userSelect: 'none',
                  }}
                >
                  <Box sx={{ textAlign: 'center', px: 0.5, lineHeight: 1.15 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: nameSize,
                        lineHeight: 1.1,
                        color: '#0F172A',
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: seatsSize,
                        fontWeight: 600,
                        color: '#475569',
                      }}
                    >
                      {capacity} seats
                    </Typography>
                  </Box>
                </Box>
                {selected ? (
                  <>
                    {RESIZE_CORNERS.map((corner) => (
                      <Box
                        key={corner.id}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          snapshotHistory?.();
                          dragRef.current = {
                            kind: 'resize-table',
                            id: uuid,
                            corner: corner.id,
                            startX: e.clientX,
                            startY: e.clientY,
                            orig: { ...placement },
                          };
                        }}
                        sx={cornerHandleSx(corner)}
                      />
                    ))}
                    <Box
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        snapshotHistory?.();
                        const rect =
                          e.currentTarget.parentElement.getBoundingClientRect();
                        dragRef.current = {
                          kind: 'rotate-table',
                          id: uuid,
                          centerX: rect.left + rect.width / 2,
                          centerY: rect.top + rect.height / 2,
                          orig: { ...placement },
                        };
                      }}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: -22,
                        width: 12,
                        height: 12,
                        ml: '-6px',
                        bgcolor: '#0EA5E9',
                        border: '1px solid #fff',
                        borderRadius: '50%',
                        cursor: 'grab',
                        zIndex: 2,
                      }}
                    />
                  </>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FloorPlanCanvas;
